import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowRight, Users } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { BudgetBurn } from "@/components/charts/BudgetBurn";
import { HoursByPerson } from "@/components/charts/HoursByPerson";
import { MarginTrend } from "@/components/charts/MarginTrend";
import { PlannedVsActual } from "@/components/charts/PlannedVsActual";
import { RevenueCostBars } from "@/components/charts/RevenueCostBars";
import { TrendArea } from "@/components/charts/TrendArea";
import { PageHeaderBar, PageHeaderMetric } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { PnlStatementTable } from "@/components/profitability/AccountPnl";
import { BudgetCell } from "@/components/profitability/BudgetCell";
import { ClientPartnerCell } from "@/components/profitability/ClientPartnerCell";
import { projectClientPartner } from "@/components/profitability/clientPartner";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import {
  ActiveFilterBar,
  FilterButton,
  FilterDialog,
} from "@/components/profitability/FilterDialog";
import { ForecastBadge } from "@/components/profitability/ForecastBadge";
import {
  activeFilterCount,
  applyFilters,
  buildFilterOptions,
  emptyFilterValues,
  type FilterDef,
} from "@/components/profitability/filters";
import { MarginBadge } from "@/components/profitability/MarginBadge";
import { MarginCell } from "@/components/profitability/MarginCell";
import { MarginFigure } from "@/components/profitability/MarginFigure";
import { ProjectMonthToDateCard } from "@/components/profitability/MonthToDate";
import { monthDrillIn, peopleInPeriod } from "@/components/profitability/monthDrillIn";
import { PeopleRollup } from "@/components/profitability/PeopleRollup";
import { ProjectProjectedMarginCard } from "@/components/profitability/ProjectedMargin";
import { rollUpProjects } from "@/components/profitability/rollUpProjects";
import { ProjectScheduledBillingPanel } from "@/components/profitability/ScheduledBilling";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PanelTitle } from "@/components/shared/panel-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClientSort } from "@/hooks/use-client-sort";
import { FILTER_PARAM_PREFIX, useFilterParams } from "@/hooks/use-filter-params";
import { useHidesPersonLevel, useMayRequestPnl } from "@/hooks/use-my-access";
import { useProjectDetail } from "@/hooks/use-project-detail";
import { useProjectPnl } from "@/hooks/use-project-pnl";
import { useRangeParam } from "@/hooks/use-range-param";
import { useTabParam } from "@/hooks/use-tab-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours, money, optionalHours, optionalMoney, percent, rate } from "@/lib/format";
import type {
  ClientPartner,
  ProjectDetail,
  ProjectPersonRow,
  ProjectRow,
} from "@/lib/profitability-api";

/**
 * The filters the People tab offers, and the whole of what it takes to add one.
 *
 * Both are derived from the rows the page already holds -- never a fixed list
 * of months or names, which is how a filter comes to offer a choice that
 * returns nothing. Module-level so the array identity is stable across renders;
 * the filter hook and the sort memo both take it as a dependency.
 *
 * Adding "billable only" or "rate set / no rate" later is one more entry here.
 */
const PEOPLE_FILTERS: FilterDef<ProjectPersonRow>[] = [
  {
    key: "month",
    label: "Month",
    valueOf: (row) => row.period,
    // period_start, NOT period: "April 2024" sorts before "December 2023" by
    // name. That bug has shipped twice in this codebase already.
    sortBy: (row) => row.period_start ?? row.period,
    descending: true,
    help: "The close months this project booked hours in, newest first.",
  },
  {
    key: "person",
    label: "Person",
    valueOf: (row) => row.person,
    help: "Everyone who booked hours to this project in the selected range.",
  },
];

const PEOPLE_FILTER_KEYS = PEOPLE_FILTERS.map((def) => def.key);

/** Which tab each filter set belongs to. A new tab's filters are one entry. */
const FILTERS_BY_TAB: Record<string, FilterDef<ProjectPersonRow>[]> = {
  people: PEOPLE_FILTERS,
};

/**
 * Where the budget came from, in one sentence per source.
 *
 * An "over budget" verdict against a summed monthly plan is a weaker claim
 * than one against a number someone set, and the screen has to say which it is
 * rather than let the reader assume.
 *
 * Exported and shared by every place the budget is named. The guidance wording
 * used to open "no budget was set on the demand", which is literally true and
 * read as "this project has no budget" -- beside a chart drawing an 11.7k-hour
 * budget line. Lead with what the budget IS; the absence is the subordinate
 * clause. One string, reused, is also why the page can no longer describe the
 * same number three different ways on three cards.
 */
export const BUDGET_BASIS: Record<NonNullable<ProjectRow["budget_source"]> | "none", string> = {
  demand: "Budget set on the demand.",
  demand_plan: "Budget snapshotted on the demand plan.",
  guidance: "Budget inferred by summing the monthly plan; none was set on the demand itself.",
  none: "No budget on record.",
};

/**
 * Publishes the project's identity and its profit into the app header.
 *
 * Separate component for the same reason the account one is: the page returns
 * early while loading, and a hook cannot live after a conditional return.
 *
 * No benchmark here: none is agreed at project level, so MarginBadge is given
 * null and states the margin without judging it.
 */
function ProjectHeader({
  name,
  accountName,
  accountId,
  owner,
  profit,
  marginPct,
  isClosed,
  rangeInfo,
  range,
  onRangeChange,
  filters,
}: {
  name: string;
  accountName: string | null;
  accountId: string | null;
  /** The project's own inferred Client Partner, or the account's, or null. */
  owner: ClientPartner | null;
  /** Null for Aggregate users: no project cost, so no profit -- margin only. */
  profit: number | null;
  marginPct: number | null;
  isClosed: boolean | null;
  rangeInfo: ProjectDetail["range"];
  range: string;
  onRangeChange: (key: string) => void;
  /** The Filters control, or null on a tab that has no filters to offer. */
  filters: ReactNode;
}) {
  const rangeLabel = rangeInfo?.label ?? "";
  usePageHeader(
    () => (
      <PageHeaderBar
        // The same shape as the account header, deliberately: the two pages are
        // one screen at two altitudes, and a reader moving between them should
        // not have to re-find the profit figure.
        eyebrow={
          accountId ? (
            <Link
              to="/dashboard/accounts/$accountId"
              params={{ accountId }}
              className="w-fit truncate text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              {accountName ?? accountId}
            </Link>
          ) : accountName ? (
            <span className="truncate text-xs text-muted-foreground">{accountName}</span>
          ) : null
        }
        title={name}
        titleBadge={isClosed !== null ? <ForecastBadge isClosed={isClosed} /> : null}
        fields={[
          {
            // Most projects show the ACCOUNT's partner here, marked "via
            // account" by the cell itself: the normal case, not a defect.
            key: "owner",
            label: "Client Partner",
            content: <ClientPartnerCell owner={owner} className="text-sm font-medium" />,
            widthClass: "max-w-[12rem]",
          },
          profit === null
            ? {
                key: "margin",
                label: `Margin${rangeInfo ? ` · ${rangeLabel}` : ""}`,
                content: <MarginBadge value={marginPct} benchmark={null} />,
              }
            : {
                key: "profit",
                label: `Profit${rangeInfo ? ` · ${rangeLabel}` : ""}`,
                content: (
                  <PageHeaderMetric
                    value={money(profit)}
                    // No benchmark: none is agreed at project level, so the badge
                    // states the margin without judging it.
                    badge={<MarginBadge value={marginPct} benchmark={null} />}
                  />
                ),
              },
        ]}
        // No contract-type filter: a demand has exactly one billing type, so
        // filtering by it could only return this project or nothing.
        filters={filters}
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={onRangeChange}
      />
    ),
    [
      name,
      accountName,
      accountId,
      owner,
      profit,
      marginPct,
      isClosed,
      rangeInfo,
      rangeLabel,
      range,
      onRangeChange,
      filters,
    ],
  );
  return null;
}

/**
 * A dash, with the reason it is a dash.
 *
 * Every missing figure on these screens is missing for a knowable reason -- no
 * rate on the assignment, no plan for the month, no budget on the demand -- and
 * a bare em dash makes the reader guess between "zero" and "unknown". Zero is
 * never written for an absence.
 */
/**
 * A project NOS knows about that has not been through a month-end close: the
 * engine sends its page with no closed periods (a T&M project in its first
 * month, linked from This month). The closed-figure cards and charts say so
 * plainly rather than drawing empty axes; the This month card above them is
 * the live picture.
 */
const NO_CLOSED_TITLE = "No closed months yet";
const NO_CLOSED_TEXT =
  "Closed-month figures appear here after this project's first month-end close.";

function NoClosedMonths() {
  return (
    <Card data-testid="no-closed-months" className="w-full">
      <CardContent>
        <EmptyState title={NO_CLOSED_TITLE} description={NO_CLOSED_TEXT} />
      </CardContent>
    </Card>
  );
}

function Dash({ reason }: { reason: string }) {
  return (
    <span className="text-muted-foreground" title={reason}>
      &mdash;
    </span>
  );
}

/** One labelled figure in a drill-in's headline row. */
function Figure({ label, value, help }: { label: string; value: ReactNode; help: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2" title={help}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// A stable empty array for the sort hooks while the page is still loading.
// A fresh [] each render would give useMemo a new dependency every time.
const EMPTY_ROWS: never[] = [];

/** The tabs an Aggregate user gets: the rest are built on hours and people. */
const AGGREGATE_TABS = new Set(["overview", "trend"]);

export function ProjectDetailPage() {
  const { demandId } = useParams({ from: "/dashboard/projects/$demandId" });
  const navigate = useNavigate();
  const [range, setRange] = useRangeParam();
  const [tabParam, setTab] = useTabParam("overview");
  const { data, isLoading, isError, error, refetch } = useProjectDetail(demandId, range);
  // Aggregate users (Client Partners, CSMs) see a project's revenue, margin %,
  // budget burn % and billing -- never its cost, hours, people or P&L, which on
  // a small project are one person's pay. The engine omits those keys; this
  // page drops the tabs, cards, charts and drill-ins built on them, and a
  // pasted link to a hidden tab lands on Overview.
  const hidesPersonLevel = useHidesPersonLevel(data?.access);
  const mayRequestPnl = useMayRequestPnl(data?.access);
  const tab = hidesPersonLevel && !AGGREGATE_TABS.has(tabParam) ? "overview" : tabParam;
  // Fetched only once the tab is opened -- a second round trip to the engine
  // that most visits to this page never need. Same arrangement as the account.
  const pnl = useProjectPnl(demandId, range, tab === "pnl" && mayRequestPnl);
  const [filterValues, setFilterValues] = useFilterParams(PEOPLE_FILTER_KEYS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The two drill-ins. A month (clicked on any of the four month charts) and a
  // person (clicked on the hours-by-person bars) are the only two subjects the
  // charts on this page have.
  const [focusPeriod, setFocusPeriod] = useState<string | null>(null);
  const [focusPerson, setFocusPerson] = useState<string | null>(null);

  /**
   * Leave the drill-in for the People tab, filtered to what was being read.
   *
   * ONE navigation, not `setFilterValues` followed by `setTab`: both resolve
   * their `prev` against the same location, so the second would spread a search
   * object that does not yet carry the first and drop the filter on the floor.
   */
  const openInPeople = (key: "month" | "person", value: string) => {
    setFocusPeriod(null);
    setFocusPerson(null);
    navigate({
      to: ".",
      replace: true,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: "people",
        // Only the one filter the reader drilled on; anything already set is
        // cleared, or the tab opens on an intersection they never asked for.
        [`${FILTER_PARAM_PREFIX}month`]: key === "month" ? [value] : undefined,
        [`${FILTER_PARAM_PREFIX}person`]: key === "person" ? [value] : undefined,
      }),
    });
  };

  // Filtering is client-side over rows the page already holds: the detail
  // endpoint returns every person-month for the range in one payload, so a
  // refetch would re-download the same rows to return a subset of them, and
  // narrowing server-side would make the option lists -- which are built from
  // the rows on the page -- shrink to whatever is already selected.
  const allPeople = data?.people ?? EMPTY_ROWS;
  const filteredPeople = useMemo(
    () => applyFilters(allPeople, PEOPLE_FILTERS, filterValues),
    [allPeople, filterValues],
  );

  // No back link: the account is linked in the header, and Projects is in the
  // sidebar. The loaded view dropped it, so these branches must match or it
  // flashes in on every load.
  const periodSort = useClientSort(
    data?.periods ?? EMPTY_ROWS,
    (row, key) =>
      key === "period"
        ? (row.period_start ?? row.period ?? "")
        : (row[key as keyof typeof row] as number | null),
    { key: "period", dir: "desc" },
  );
  const peopleSort = useClientSort(
    filteredPeople,
    (row, key) =>
      key === "period"
        ? (row.period_start ?? row.period ?? "")
        : (row[key as keyof typeof row] as string | number | null),
    { key: "period", dir: "desc" },
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Newest first for the trend table -- the accounts and projects grids both
  // read newest-to-oldest, so a project's own history should too.

  // Charts read oldest-to-newest. Sort on period_start, the real date -- NOT on
  // `period`, which is a display name: "April 2024" sorts before "December
  // 2023" alphabetically. That bug has appeared twice in this codebase.
  const chronological = [...data.periods].sort((a, b) =>
    (a.period_start ?? a.period ?? "").localeCompare(b.period_start ?? b.period ?? ""),
  );
  // The most recent period in the range, closed or an in-flight forecast. Taken
  // from the sorted list, not data.periods.at(-1): the detail endpoint happens
  // to return oldest-first today, and a card headed "Margin (December 2025)"
  // has no way to look wrong if that ever changes.
  const latest = chronological.at(-1) ?? null;
  // Options come from EVERY person-month the range holds, not the filtered set:
  // built from the filtered rows, the modal would only ever offer back the
  // choices already made, with no way to widen a filter once it is on.
  const people = data.people ?? EMPTY_ROWS;
  const peopleFilterOptions = buildFilterOptions(people, PEOPLE_FILTERS);
  const peopleFiltersOn = activeFilterCount(filterValues) > 0;
  const tabFilters = hidesPersonLevel ? null : (FILTERS_BY_TAB[tab] ?? null);
  const marginPoints = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    value: row.margin_pct,
    id: row.period_id,
    provisional: row.period_is_closed === false,
  }));
  const moneyByMonth = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    revenue: row.revenue,
    // These three series feed charts that only render when cost and hours are
    // on the page (never for Aggregate), so the fallback is never drawn.
    cost: row.cost ?? 0,
    id: row.period_id,
    provisional: row.period_is_closed === false,
  }));
  const burn = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    actualHours: row.hours ?? 0,
    id: row.period_id,
  }));
  // The budget is set once for the demand, so every period row repeats it.
  // Take it from the latest row that has one rather than per month.
  const latestWithBudget = [...chronological].reverse().find((row) => row.has_budget);

  const projectBudgetHours = latestWithBudget?.budget_hours ?? null;
  const budgetSource = latestWithBudget?.budget_source ?? null;
  // Guidance is the only per-month figure, so it is the only one a monthly
  // comparison can use.
  const plannedByMonth = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    plannedHours: row.planned_hours ?? null,
    actualHours: row.hours ?? 0,
    id: row.period_id,
  }));
  // Profit and margin over every period on the page, to match the account
  // header. The same roll-up the account's projects table uses: from summed
  // cost when there is cost, and for Aggregate (no cost) the revenue-weighted
  // monthly margin -- with no profit figure at all.
  const rolled = rollUpProjects(data.periods)[0];
  const totalProfit =
    rolled === undefined || rolled.cost === null ? null : rolled.revenue - rolled.cost;
  const totalMarginPct = rolled?.marginPct ?? null;
  // From the latest row when there is one; a first-month project has none,
  // and the engine's project block carries the same facts from NOS.
  const accountId = latest?.account_id ?? data.project.account_id ?? null;
  const clientPartner = projectClientPartner(data.periods) ?? data.project.client_partner ?? null;

  // The month drill-in, shared by all four month charts. A month focused under
  // a wider range need not exist in a narrower one; `monthDrillIn` returns null
  // for that rather than leaving an empty modal open.
  const focus = monthDrillIn(chronological, focusPeriod, projectBudgetHours);
  const focusPeople = focus ? peopleInPeriod(people, focus.row) : [];
  const focusPeopleHours = focusPeople.reduce((sum, row) => sum + row.hours, 0);
  // The person drill-in: every month this person booked to THIS project, newest
  // first, sorted on the real date and not the month name.
  const personRows = focusPerson
    ? people
        .filter((row) => row.person === focusPerson)
        .sort((a, b) =>
          (b.period_start ?? b.period ?? "").localeCompare(a.period_start ?? a.period ?? ""),
        )
    : [];
  const personTotals = personRows.reduce(
    (acc, row) => ({
      hours: acc.hours + row.hours,
      billable: acc.billable + row.billable_hours,
      cost: acc.cost + row.cost,
      value: acc.value + (row.billable_value ?? 0),
      // Counted, not assumed: a person with no rate on some assignments makes
      // the value total a partial answer, and the row has to say so.
      unpriced: acc.unpriced + (row.billable_value === null ? 1 : 0),
    }),
    { hours: 0, billable: 0, cost: 0, value: 0, unpriced: 0 },
  );

  return (
    <div className="w-full space-y-4">
      <ProjectHeader
        name={data.project.project_name ?? data.project.demand_id}
        accountName={data.project.account_name}
        accountId={accountId}
        owner={clientPartner}
        profit={totalProfit}
        marginPct={totalMarginPct}
        isClosed={latest?.period_is_closed ?? null}
        rangeInfo={data.range}
        range={range}
        onRangeChange={setRange}
        filters={
          // Only on a tab that has filters. A Filters button on Overview would
          // open a modal whose every choice changes nothing on screen.
          tabFilters ? (
            <FilterButton values={filterValues} onOpen={() => setFiltersOpen(true)} />
          ) : null
        }
      />

      {tabFilters ? (
        <FilterDialog
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          defs={tabFilters}
          options={peopleFilterOptions}
          values={filterValues}
          onApply={setFilterValues}
          scopeNote="Filters the two People tables below. The profit in the header and the other tabs cover the whole project over the selected range and do not change."
        />
      ) : null}

      {/* Four questions, four tabs. Eight cards in one column meant the
          People roll-up -- the thing that explains a cost problem -- sat
          three screens below the margin that prompted the question. */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {hidesPersonLevel ? null : (
            <>
              <TabsTrigger value="budget">Budget &amp; hours</TabsTrigger>
              <TabsTrigger value="people">People</TabsTrigger>
            </>
          )}
          <TabsTrigger value="trend">Trend</TabsTrigger>
          {/* Its own tab for the same reason the account's is: the statement
              answers "what does finance have booked against this demand", not
              "how is delivery doing", and its revenue need not agree with the
              delivery figures on the other tabs. */}
          {hidesPersonLevel ? null : <TabsTrigger value="pnl">P&amp;L</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* First on the page: where this project stands now, styled apart
              from the closed figures below because both are forecasts and
              neither is ever added into them. T&M: the open month, live
              (null otherwise). Fixed price: the whole contract against its
              whole cost, projected (null otherwise), beside the billing
              schedule because the two read the same billing lines. */}
          {data.month_to_date ? (
            <ProjectMonthToDateCard
              slice={data.month_to_date}
              contractType={latest?.billing_type ?? null}
            />
          ) : null}
          {data.projected_margin ? (
            <div className="grid w-full grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <ProjectProjectedMarginCard projection={data.projected_margin} />
              <ProjectScheduledBillingPanel schedule={data.scheduled_billing} />
            </div>
          ) : null}

          {latest ? (
            <div
              className={
                hidesPersonLevel
                  ? "grid grid-cols-1 gap-4 sm:grid-cols-2"
                  : "grid grid-cols-1 gap-4 sm:grid-cols-3"
              }
            >
              {/* This card used to show the LATEST MONTH's margin under the
                  bare heading "Margin", two inches below the header's
                  whole-range margin, also bare. 50.4% and 31.4% on one screen,
                  neither saying what it covered. The card now leads with the
                  same range the header does -- so the two tie -- and carries
                  the month as a subordinate figure that names its own period. */}
              <Card>
                <CardHeader>
                  <PanelTitle>Margin ({data.range?.label ?? "selected range"})</PanelTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-display text-[36px] font-semibold tracking-tight">
                    <MarginFigure value={totalMarginPct} />
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Latest month ({latest.period ?? latest.period_id}):{" "}
                    <MarginCell value={latest.margin_pct} />
                  </p>
                  {marginPoints.filter((p) => p.value !== null).length >= 2 ? (
                    <div className="mt-3">
                      <TrendArea points={marginPoints} benchmark={null} height={90} />
                    </div>
                  ) : null}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <PanelTitle>Budget vs. actual (project to date)</PanelTitle>
                </CardHeader>
                <CardContent>
                  {/* Project to date, NOT the selected range: variance_pct is one
                      project-lifetime figure repeated on every period row, so
                      narrowing the range does not narrow it. The title has to
                      say that or the card silently reads as range-scoped like
                      the two beside it. */}
                  <BudgetCell
                    variancePct={latest.variance_pct}
                    hasBudget={latest.has_budget}
                    basis={BUDGET_BASIS[budgetSource ?? "none"]}
                    barWidth={120}
                  />
                  {typeof latest.budget_hours === "number" &&
                  Number.isFinite(latest.budget_hours) ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Budget: {hours(latest.budget_hours)} h
                    </p>
                  ) : null}
                </CardContent>
              </Card>
              {hidesPersonLevel ? null : (
                <Card>
                  <CardHeader>
                    <PanelTitle>Hours</PanelTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    {/* Two different scopes, shown as such rather than implied to be
                    comparable: billable_hours is a project-lifetime figure from
                    the timesheet source (rates_by_demand groups by demand only,
                    not by period), while hours is this one period's total from
                    demand_margin. Subtracting one from the other would combine
                    two sources that are not guaranteed to reconcile. */}
                    <div>
                      Billable (project-to-date):{" "}
                      <span className="tabular-nums">{optionalHours(latest.billable_hours)}</span>
                    </div>
                    <div>
                      Total ({latest.period ?? latest.period_id}):{" "}
                      <span className="tabular-nums">{optionalHours(latest.hours)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}

          {/* Not fixed price: the schedule (usually small pass-through fees)
              follows the closed figures rather than leading the page. */}
          {data.projected_margin ? null : (
            <ProjectScheduledBillingPanel schedule={data.scheduled_billing} />
          )}

          {/* Charts first: the shape is what gets read, the tables below are for
            looking up an exact figure afterwards. */}
          {data.periods.length === 0 ? (
            <NoClosedMonths />
          ) : (
            <div
              className={
                // One chart for Aggregate (margin only), so it takes the full width.
                hidesPersonLevel ? "grid w-full gap-4" : "grid w-full gap-4 xl:grid-cols-2"
              }
            >
              <Card className="w-full py-4">
                <CardContent>
                  <MarginTrend
                    points={marginPoints}
                    benchmark={data.benchmark_pct}
                    // No month drill-in for Aggregate: it is cost, hours and
                    // people for the month, none of which they see.
                    subtitle={
                      hidesPersonLevel
                        ? undefined
                        : "Click a month for its revenue, cost and who booked time"
                    }
                    onSelect={
                      hidesPersonLevel ? undefined : (point) => setFocusPeriod(point.id ?? null)
                    }
                  />
                </CardContent>
              </Card>
              {hidesPersonLevel ? null : (
                <Card className="w-full py-4">
                  <CardContent>
                    {/* The same modal the margin chart opens, deliberately: the two
                    charts plot the same months from the same rows, and two
                    drill-ins would be two places for one month's figures to be
                    written -- and, eventually, to disagree. */}
                    <RevenueCostBars
                      months={moneyByMonth}
                      subtitle="The gap between the bars is the margin. Click a month to open it."
                      onSelect={(month) => setFocusPeriod(month.id ?? null)}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {hidesPersonLevel ? null : (
          <>
            <TabsContent value="budget" className="space-y-4">
              {data.periods.length === 0 ? (
                <NoClosedMonths />
              ) : (
                <div className="grid w-full gap-4 xl:grid-cols-2">
                  <Card className="w-full py-4">
                    <CardContent>
                      {/* Same basis sentence and the same variance figure as the
                    Overview card -- passed in rather than re-derived, because
                    the chart's own cumulative covers the selected range while
                    the budget covers the whole project. */}
                      <BudgetBurn
                        periods={burn}
                        budgetHours={projectBudgetHours}
                        variancePct={latest?.variance_pct ?? null}
                        rangeLabel={data.range?.label ?? null}
                        subtitle={`${BUDGET_BASIS[budgetSource ?? "none"]} Click a month to open it.`}
                        onSelect={(point) => setFocusPeriod(point.id ?? null)}
                      />
                    </CardContent>
                  </Card>
                  <Card className="w-full py-4">
                    <CardContent>
                      {/* A different basis from the burn-up beside it, said out loud:
                    per month against guidance, not whole project against the
                    budget. Unlabelled, "Over plan: Jan, Aug" and "31.6% under
                    budget" read as a contradiction rather than two measures. */}
                      <PlannedVsActual
                        months={plannedByMonth}
                        subtitle="Each month against its own planned hours, not against the project budget. Click a month to open it."
                        onSelect={(month) => setFocusPeriod(month.id ?? null)}
                      />
                    </CardContent>
                  </Card>
                  {data.people_withheld ? null : (
                    <Card className="w-full py-4">
                      <CardContent>
                        <HoursByPerson
                          people={people}
                          subtitle="Click a name for their months, cost and value at rate"
                          onSelect={(person) => setFocusPerson(person)}
                        />
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="people" className="space-y-4">
              {/* Directly above the tables, not only in the header: a reader who has
              scrolled past the header is otherwise looking at a subset with
              nothing on screen saying so. */}
              <ActiveFilterBar
                defs={PEOPLE_FILTERS}
                values={filterValues}
                shown={filteredPeople.length}
                total={people.length}
                onEdit={() => setFiltersOpen(true)}
                onClear={() => setFilterValues(emptyFilterValues(PEOPLE_FILTERS))}
              />

              {data.people_withheld ? null : (
                <Card flush className="w-full">
                  <CardHeader>
                    {/* The title carries the basis, not just the paragraph under it:
                    this table's Total row shows a margin, and a reader
                    scanning past a card headed "People" took that 36.9% for
                    the project's margin. It is a different measure, so the
                    heading has to say which one before the number is read. */}
                    <PanelTitle help="The selected months, at each person's contracted rate. Value is hours x rate -- what the time is worth at the agreed rate, not what the client was invoiced, so the Total margin here is a rate-card margin and will not tie to the project margin in the header. That one is the billed answer; on a fixed-fee engagement the two are not expected to agree.">
                      People &mdash; margin at contracted rates
                    </PanelTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Filtered rows, so every figure in the Total row -- which is
                    summed from exactly these rows -- covers the filtered set
                    rather than the project. `filtered` is what makes the row
                    say so. */}
                    <PeopleRollup rows={filteredPeople} filtered={peopleFiltersOn} />
                  </CardContent>
                </Card>
              )}

              <Card flush className="w-full">
                <CardHeader>
                  <PanelTitle>People by month</PanelTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {data.people_withheld ? (
                    <p className="px-4 py-6 text-sm text-muted-foreground">
                      Person-level cost is not available at your access level.
                    </p>
                  ) : (
                    <DataTable
                      flush
                      headers={[
                        { label: "Person", sortKey: "person", help: COLUMN_HELP.person },
                        { label: "Period", sortKey: "period", help: COLUMN_HELP.period },
                        {
                          label: "Hours",
                          className: "text-right",
                          sortKey: "hours",
                          help: COLUMN_HELP.personHours,
                        },
                        {
                          label: "Billable",
                          className: "text-right",
                          sortKey: "billable_hours",
                          // Both halves of the cell: the figure, and the
                          // non-billable note that sits beside it.
                          help: `${COLUMN_HELP.billableHours} ${COLUMN_HELP.nonBillableNote}`,
                        },
                        {
                          label: "Loaded hourly cost",
                          className: "text-right",
                          sortKey: "hourly_cost",
                          help: COLUMN_HELP.costRate,
                        },
                      ]}
                      sort={peopleSort.sort}
                      sortDir={peopleSort.sortDir}
                      onSort={peopleSort.toggleSort}
                      empty={
                        // Under a filter the truth is "nothing matched", not "no
                        // one is assigned" -- the second is a claim about the
                        // project that the filter has no standing to make.
                        peopleFiltersOn ? (
                          <EmptyState
                            icon={Users}
                            title="No one matches this filter"
                            description="No hours on this project match the filters that are on. Clear or widen them to see the rest."
                          />
                        ) : (
                          <EmptyState
                            icon={Users}
                            title="No one assigned"
                            description="No one is assigned to this project."
                          />
                        )
                      }
                    >
                      {peopleSort.rows.map((row) => (
                        <tr
                          key={`${row.person}-${row.period}`}
                          className="border-b border-border last:border-0"
                        >
                          <td className="px-4 py-3 font-medium">{row.person}</td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-2">
                              {row.period}
                              <ForecastBadge isClosed={row.period_is_closed} />
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{hours(row.hours)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {hours(row.billable_hours)}
                            {row.non_billable_hours > 0.01 ? (
                              // Spelled out, not "nb". The abbreviation appeared
                              // nowhere else on the page to decode it, and the
                              // hover that did explain it was on a five-character
                              // grey span nobody would think to point at.
                              <span
                                className="ml-1 text-xs text-muted-foreground"
                                title={`${hours(row.non_billable_hours)} non-billable hours this month: worked and costed, earning nothing.`}
                              >
                                (+{row.non_billable_hours.toFixed(0)} non-billable)
                              </span>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {row.hourly_cost === null ? "—" : rate(row.hourly_cost)}
                          </td>
                        </tr>
                      ))}
                    </DataTable>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        <TabsContent value="trend" className="space-y-4">
          <Card flush className="w-full">
            <CardHeader>
              <PanelTitle>Margin by period</PanelTitle>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable
                flush
                headers={[
                  { label: "Period", sortKey: "period", help: COLUMN_HELP.period },
                  {
                    label: "Revenue",
                    className: "text-right",
                    sortKey: "revenue",
                    help: COLUMN_HELP.revenue,
                  },
                  ...(hidesPersonLevel
                    ? []
                    : [
                        {
                          label: "Cost",
                          className: "text-right",
                          sortKey: "cost",
                          help: COLUMN_HELP.cost,
                        },
                      ]),
                  {
                    label: "Margin",
                    className: "text-right",
                    sortKey: "margin_pct",
                    help: COLUMN_HELP.margin,
                  },
                  ...(hidesPersonLevel
                    ? []
                    : [
                        {
                          label: "Hours",
                          className: "text-right",
                          sortKey: "hours",
                          help: COLUMN_HELP.hours,
                        },
                      ]),
                  // Replaces a Budget column that showed variance_pct -- a
                  // PROJECT-level figure repeated on every row, so it read as the
                  // identical -31.6% twelve times over. Planned hours are the only
                  // budget figure that varies by month.
                  ...(hidesPersonLevel
                    ? []
                    : [
                        {
                          label: "Planned",
                          className: "text-right",
                          sortKey: "planned_hours",
                          help: COLUMN_HELP.plannedHours,
                        },
                      ]),
                ]}
                sort={periodSort.sort}
                sortDir={periodSort.sortDir}
                onSort={periodSort.toggleSort}
                empty={<EmptyState title={NO_CLOSED_TITLE} description={NO_CLOSED_TEXT} />}
              >
                {periodSort.rows.map((row) => (
                  <tr key={row.period_id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        {row.period ?? row.period_id}
                        <ForecastBadge isClosed={row.period_is_closed} />
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(row.revenue)}</td>
                    {hidesPersonLevel ? null : (
                      <td className="px-4 py-3 text-right tabular-nums">
                        {optionalMoney(row.cost)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <MarginCell value={row.margin_pct} />
                    </td>
                    {hidesPersonLevel ? null : (
                      <td className="px-4 py-3 text-right tabular-nums">
                        {optionalHours(row.hours)}
                      </td>
                    )}
                    {hidesPersonLevel ? null : (
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.planned_hours == null ? (
                          <span className="text-muted-foreground" title="No plan for this month">
                            &mdash;
                          </span>
                        ) : (
                          hours(row.planned_hours)
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </DataTable>
            </CardContent>
          </Card>
        </TabsContent>

        {hidesPersonLevel ? null : (
          <TabsContent value="pnl" className="space-y-4">
            <Card flush className="w-full">
              <CardHeader>
                <PanelTitle>Profit &amp; loss</PanelTitle>
                {/* Two scope traps, both stated rather than left to be inferred.
                  The year columns ignore the range selector and the last column
                  does not; and this statement's revenue is what finance
                  INVOICED against the demand, which on fixed-fee work is not
                  the recognised revenue the Trend tab reports. */}
                <p className="text-sm text-muted-foreground">
                  NOS's own statement for this project, in NOS's order. Year columns are whole
                  calendar years; the last column is the range selected above. Subtotals come from
                  NOS, not from summing the rows.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {pnl.isError ? (
                  <div className="p-4">
                    <ErrorState
                      message={(pnl.error as Error)?.message}
                      onRetry={() => pnl.refetch()}
                    />
                  </div>
                ) : pnl.isLoading || !pnl.data ? (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <PnlStatementTable
                    pnl={pnl.data}
                    rangeLabel={data.range?.label ?? ""}
                    emptyDescription="NOS has no profit-and-loss lines booked against this project."
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Both modals sit outside the tabs: a drill-in opened from an Overview
          chart must not unmount when its tab loses focus. */}
      {hidesPersonLevel ? null : (
        <>
          <DrillInDialog
            open={focus !== null}
            onOpenChange={(open: boolean) => !open && setFocusPeriod(null)}
            title={focus ? (focus.row.period ?? focus.row.period_id) : ""}
            description="Everything this project booked in the month you clicked."
            // A month has no page of its own, so the way onward is the People tab
            // filtered to it -- where the reader can sort, filter and read the
            // roll-up rather than the five columns this modal has room for.
            action={
              focus?.row.period ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openInPeople("month", focus.row.period as string)}
                >
                  See this month in People
                  <ArrowRight className="size-3.5" aria-hidden />
                </Button>
              ) : null
            }
          >
            {focus ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <Figure
                    label="Revenue"
                    value={money(focus.row.revenue)}
                    help={COLUMN_HELP.revenue}
                  />
                  <Figure
                    label="Cost"
                    value={optionalMoney(focus.row.cost)}
                    help={COLUMN_HELP.cost}
                  />
                  <Figure
                    label="Profit"
                    value={optionalMoney(
                      focus.row.cost === undefined ? undefined : focus.row.revenue - focus.row.cost,
                    )}
                    help={COLUMN_HELP.profit}
                  />
                  <Figure
                    label="Margin"
                    value={<MarginCell value={focus.row.margin_pct} />}
                    help={COLUMN_HELP.margin}
                  />
                  <Figure
                    label="Hours"
                    value={optionalHours(focus.row.hours)}
                    help={COLUMN_HELP.hours}
                  />
                </div>

                {/* The budget half of the month, and the scope split it turns on:
                the running total covers the SELECTED RANGE, the budget covers
                the whole project, so "remaining" is a ceiling and the
                project-to-date variance beside it is the verdict. */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Figure
                    label="Planned this month"
                    value={
                      focus.row.planned_hours == null ? (
                        <Dash reason="No plan for this month" />
                      ) : (
                        hours(focus.row.planned_hours)
                      )
                    }
                    help={COLUMN_HELP.plannedHours}
                  />
                  <Figure
                    label={`Burned by ${focus.row.period ?? "this month"}`}
                    value={hours(focus.cumulativeHours)}
                    help={COLUMN_HELP.cumulativeHours}
                  />
                  <Figure
                    label="Budget, whole project"
                    value={
                      projectBudgetHours === null ? (
                        <Dash reason={BUDGET_BASIS.none} />
                      ) : (
                        hours(projectBudgetHours)
                      )
                    }
                    help={BUDGET_BASIS[budgetSource ?? "none"]}
                  />
                  <Figure
                    label="Remaining"
                    value={
                      focus.remainingHours === null ? (
                        <Dash reason="No budget on record, so nothing to remain against." />
                      ) : (
                        hours(focus.remainingHours)
                      )
                    }
                    help={COLUMN_HELP.budgetRemaining}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {latest?.variance_pct === null || latest?.variance_pct === undefined
                    ? "Project to date: no variance on record."
                    : `Project to date the demand is ${percent(Math.abs(latest.variance_pct))} ${
                        latest.variance_pct > 0 ? "over" : "under"
                      } budget. Remaining above counts only the hours in ${
                        data.range?.label ?? "the selected range"
                      }.`}
                </p>

                {data.people_withheld ? (
                  <p className="text-sm text-muted-foreground">
                    Person-level cost is not available at your access level, so who booked this
                    month's hours cannot be shown.
                  </p>
                ) : (
                  <DataTable
                    headers={[
                      { label: "Person", help: COLUMN_HELP.person },
                      { label: "Hours", className: "text-right", help: COLUMN_HELP.personHours },
                      {
                        label: "Billable",
                        className: "text-right",
                        help: COLUMN_HELP.billableHours,
                      },
                      {
                        label: "Loaded cost",
                        className: "text-right",
                        help: COLUMN_HELP.personLoadedCost,
                      },
                      {
                        label: "Value at rate",
                        className: "text-right",
                        help: COLUMN_HELP.valueAtRate,
                      },
                    ]}
                    empty={
                      <EmptyState
                        icon={Users}
                        title="Nobody booked time this month"
                        description="The project has revenue or cost in this month, but no timesheet lines behind it."
                      />
                    }
                  >
                    {focusPeople.map((row) => (
                      <tr key={`${row.person}-${row.period}`} className="border-b last:border-0">
                        <td className="px-4 py-3">
                          {/* The name is a control, not just a label: it swaps this
                          modal for that person's own months, so a reader who
                          spots an unexpected name is one click from their
                          whole history on the project. */}
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            onClick={() => {
                              setFocusPeriod(null);
                              setFocusPerson(row.person);
                            }}
                          >
                            {row.person}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{hours(row.hours)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {hours(row.billable_hours)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{money(row.cost)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {row.billable_value === null ? (
                            <Dash reason="No contracted rate on this assignment, so the time cannot be priced." />
                          ) : (
                            money(row.billable_value)
                          )}
                        </td>
                      </tr>
                    ))}
                  </DataTable>
                )}
                {/* Two sources, so they need not tie -- said out loud rather than
                left for the reader to find by adding the column up. */}
                {!data.people_withheld && focusPeople.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {hours(focusPeopleHours)} hours from the timesheet lines above, against{" "}
                    {optionalHours(focus.row.hours)} on the month itself. The two come from
                    different tables in NOS and are not guaranteed to agree.
                  </p>
                ) : null}
              </div>
            ) : null}
          </DrillInDialog>

          <DrillInDialog
            open={focusPerson !== null}
            onOpenChange={(open: boolean) => !open && setFocusPerson(null)}
            title={focusPerson ?? ""}
            description="Their months on this project, at their contracted rate."
            action={
              focusPerson ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openInPeople("person", focusPerson)}
                >
                  See in People
                  <ArrowRight className="size-3.5" aria-hidden />
                </Button>
              ) : null
            }
          >
            <DataTable
              headers={[
                { label: "Period", help: COLUMN_HELP.period },
                { label: "Hours", className: "text-right", help: COLUMN_HELP.personHours },
                { label: "Billable", className: "text-right", help: COLUMN_HELP.billableHours },
                {
                  label: "Loaded cost",
                  className: "text-right",
                  help: COLUMN_HELP.personLoadedCost,
                },
                { label: "Value at rate", className: "text-right", help: COLUMN_HELP.valueAtRate },
              ]}
              empty={
                <EmptyState
                  icon={Users}
                  title="No months in this range"
                  description="This person booked no time to the project in the months selected."
                />
              }
            >
              {personRows.map((row) => (
                <tr key={`${row.person}-${row.period}`} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    {/* Back the other way: a month here reopens that month's own
                    drill-in, so the pair navigate between each other rather
                    than each dead-ending. */}
                    <button
                      type="button"
                      className="flex items-center gap-2 whitespace-nowrap hover:underline"
                      onClick={() => {
                        const match = chronological.find((period) =>
                          period.period_start && row.period_start
                            ? period.period_start === row.period_start
                            : period.period === row.period,
                        );
                        if (!match) return;
                        setFocusPerson(null);
                        setFocusPeriod(match.period_id);
                      }}
                    >
                      {row.period}
                      <ForecastBadge isClosed={row.period_is_closed} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{hours(row.hours)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{hours(row.billable_hours)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(row.cost)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.billable_value === null ? (
                      <Dash reason="No contracted rate on this assignment, so the time cannot be priced." />
                    ) : (
                      money(row.billable_value)
                    )}
                  </td>
                </tr>
              ))}
              {personRows.length > 0 ? (
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <th scope="row" className="px-4 py-3 text-left">
                    Total
                  </th>
                  <td className="px-4 py-3 text-right tabular-nums">{hours(personTotals.hours)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {hours(personTotals.billable)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(personTotals.cost)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {money(personTotals.value)}
                    {personTotals.unpriced > 0 ? (
                      <span
                        className="ml-1 text-xs font-normal text-muted-foreground"
                        title="Months with no contracted rate contribute nothing to this total; the value is therefore a floor, not the whole."
                      >
                        ({personTotals.unpriced} unpriced)
                      </span>
                    ) : null}
                  </td>
                </tr>
              ) : null}
            </DataTable>
          </DrillInDialog>
        </>
      )}
    </div>
  );
}
